/**
 * PiPContextMenu — a three-dot dropdown for each PiP panel giving quick access
 * to size, position, and FOV presets, all backed by code edits.
 *
 * The component is self-contained: it receives the current camera values and
 * an `onApply` callback, builds the Radix DropdownMenu, and calls onApply with
 * the new parameter values when the user picks an option. The parent translates
 * those into regex replace calls on the editor source.
 */

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '@/components/ui/dropdown-menu';
import { EllipsisVertical } from 'lucide-react';
import type { PiPCamera } from '@/worker/types';
import { PIP_GAP } from './PiPOverlays';

// ---------------------------------------------------------------------------
// Preset definitions
// ---------------------------------------------------------------------------

/** Size presets — all use a 3:2 aspect ratio matching the default slot grid. */
export const SIZE_PRESETS = [
  { label: 'Small', width: 0.2, height: 0.133 },
  { label: 'Medium', width: 0.3, height: 0.2 },
  { label: 'Large', width: 0.45, height: 0.3 },
] as const;

/**
 * Position presets — 9-cell grid (col × row).
 * Positions are computed at call-time using the panel's current width/height
 * so they stay accurate when size changes.
 */
export const POSITION_LABELS = [
  ['Top-left', 'Top-center', 'Top-right'],
  ['Mid-left', 'Center', 'Mid-right'],
  ['Bot-left', 'Bot-center', 'Bot-right'],
] as const;

/** FOV presets for perspective cameras (degrees). */
export const FOV_PRESETS = [
  { label: 'Narrow (25°)', fov: 25 },
  { label: 'Normal (45°)', fov: 45 },
  { label: 'Wide (70°)', fov: 70 },
  { label: 'Ultra-wide (90°)', fov: 90 },
] as const;

/** Zoom presets for orthographic cameras (fov field stores zoom level). */
export const ZOOM_PRESETS = [
  { label: 'Close (zoom 10)', fov: 10 },
  { label: 'Normal (zoom 20)', fov: 20 },
  { label: 'Far (zoom 40)', fov: 40 },
  { label: 'Very far (zoom 80)', fov: 80 },
] as const;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PiPParamChanges {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fov?: number;
}

// ---------------------------------------------------------------------------
// PiPContextMenu
// ---------------------------------------------------------------------------

interface PiPContextMenuProps {
  camera: PiPCamera;
  /** Called with the parameter values to change. Parent applies them via regex. */
  onApply: (changes: PiPParamChanges) => void;
}

export function PiPContextMenu({ camera, onApply }: PiPContextMenuProps) {
  // Compute position preset targets at render-time using current panel size
  const positionGrid = POSITION_LABELS.map((row, ri) =>
    row.map((label, ci) => {
      const x =
        ci === 0
          ? PIP_GAP
          : ci === 1
            ? Math.round((0.5 - camera.width / 2) * 100) / 100
            : Math.round((1 - camera.width - PIP_GAP) * 100) / 100;
      const y =
        ri === 0
          ? PIP_GAP
          : ri === 1
            ? Math.round((0.5 - camera.height / 2) * 100) / 100
            : Math.round((1 - camera.height - PIP_GAP) * 100) / 100;
      return { label, x, y };
    }),
  );

  // Current size label (for radio check)
  const currentSizeLabel =
    SIZE_PRESETS.find(
      (p) =>
        Math.abs(p.width - camera.width) < 0.001 &&
        Math.abs(p.height - camera.height) < 0.001,
    )?.label ?? '';

  // Current FOV label
  const fovPresets =
    camera.type === 'orthographic' ? ZOOM_PRESETS : FOV_PRESETS;
  const currentFovLabel =
    fovPresets.find((p) => p.fov === camera.fov)?.label ?? '';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {/*
         * The trigger is a small pill button in the top-left corner.
         * stopPropagation prevents the panel's onClick (camera fly-to) from
         * firing when the user opens the menu.
         */}
        <button
          style={{
            position: 'absolute',
            top: '4px',
            left: '4px',
            width: '20px',
            height: '20px',
            borderRadius: '4px',
            background: 'rgba(0,0,0,0.35)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'rgba(255,255,255,0.6)',
            border: 'none',
            cursor: 'pointer',
            zIndex: 10,
            touchAction: 'none',
            flexShrink: 0,
          }}
          onClick={(e) => e.stopPropagation()}
          title="PiP options"
        >
          <EllipsisVertical size={12} />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuPortal>
        <DropdownMenuContent
          side="bottom"
          align="start"
          sideOffset={6}
          className="min-w-[160px]"
          // Prevent the click that opens the menu from bubbling to the panel
          onClick={(e) => e.stopPropagation()}
        >
          <DropdownMenuLabel className="text-xs text-muted-foreground">
            PiP Camera
          </DropdownMenuLabel>
          <DropdownMenuSeparator />

          {/* ── Size ── */}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="text-sm">
              Size
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuRadioGroup
                value={currentSizeLabel}
                onValueChange={(label) => {
                  const preset = SIZE_PRESETS.find((p) => p.label === label);
                  if (preset)
                    onApply({ width: preset.width, height: preset.height });
                }}
              >
                {SIZE_PRESETS.map((p) => (
                  <DropdownMenuRadioItem key={p.label} value={p.label}>
                    {p.label}
                    <span className="ml-auto text-xs text-muted-foreground">
                      {Math.round(p.width * 100)}×{Math.round(p.height * 100)}%
                    </span>
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          {/* ── Position ── */}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="text-sm">
              Position
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {/* 3×3 visual grid of position options */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '2px',
                  padding: '4px',
                }}
              >
                {positionGrid.flat().map(({ label, x, y }) => {
                  const isActive =
                    Math.abs(camera.x - x) < 0.01 &&
                    Math.abs(camera.y - y) < 0.01;
                  return (
                    <DropdownMenuItem
                      key={label}
                      onSelect={() => onApply({ x, y })}
                      style={{
                        justifyContent: 'center',
                        fontSize: '10px',
                        padding: '4px 6px',
                        borderRadius: '4px',
                        background: isActive ? 'hsl(var(--accent))' : undefined,
                        color: isActive
                          ? 'hsl(var(--accent-foreground))'
                          : undefined,
                        minWidth: 0,
                        textAlign: 'center',
                        lineHeight: 1.2,
                      }}
                    >
                      {label}
                    </DropdownMenuItem>
                  );
                })}
              </div>
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          {/* ── FOV / Zoom ── */}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="text-sm">
              {camera.type === 'orthographic' ? 'Zoom' : 'Field of view'}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuRadioGroup
                value={currentFovLabel}
                onValueChange={(label) => {
                  const preset = fovPresets.find((p) => p.label === label);
                  if (preset) onApply({ fov: preset.fov });
                }}
              >
                {fovPresets.map((p) => (
                  <DropdownMenuRadioItem key={p.label} value={p.label}>
                    {p.label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
}
